/**
 * Navigation model (design/ux/01_foundations.md §6.3, §6.4, §6.6; WP-18).
 *
 * Pure functions over config/navigation.config.js — no React, no DOM — so the
 * rail, the drawer, the breadcrumb, the window title and the command palette
 * read ONE model and the unit tests can walk it:
 *
 *  · selectNavForUser(user)  the menu the session sees (§6.6 « Filter »):
 *      route access (ROUTE_ACCESS or the node's `roles`), `menuRoles`,
 *      `permission`, status "live"; a container with no visible child is
 *      hidden, a sub-category with one visible child is rendered as that
 *      child (a direct link carrying the container's label); `module` never
 *      hides, it only drives the lock (OP-F-10).
 *  · resolveActive(model, pathname)  the leaf the location belongs to: every
 *      leaf declares `to` + `match` patterns, the most specific pattern wins
 *      (static segments beat dynamic ones, ties go to the longest path) —
 *      replaces the longest-prefix guess of the old sidebar (AUD-NAV-14).
 *  · breadcrumbFor / windowTitleFor  §6.3 (AUD-NAV-23, AUD-NAV-24).
 *  · flattenNav(role | user)  the visible leaves with their full path (tests,
 *      palette — the WP-17 index keeps its shape).
 *  · typeAhead, cascadeKey  the keyboard model of §6.4 (APG menubar).
 *  · inAimTriangle  « menu aim » for the hover cascade.
 */
import { matchPath } from "react-router-dom";
import { CAISSIER_NAV, NAVIGATION, NAV_STATUS, NAV_TREE, pageToPath } from "@/config/navigation.config.js";
import { canAccessRoute, ROLES } from "@/config/routeAccess.config.js";
import { can as canDefault } from "@/config/permissions.config.js";
import { pharmacyHasModule, userModules } from "@/config/modules.config.js";
import { foldText } from "./combobox.js";

export const NAV_MODES = Object.freeze({ TREE: "tree", FLAT: "flat", NONE: "none" });

const isContainer = (n) => Array.isArray(n?.children);

/** Route part of a leaf target (a `to` may carry a query string). */
export const routeOf = (to) => String(to ?? "").split(/[?#]/)[0];

// ── Selection ────────────────────────────────────────────────────────────────

function leafVisible(node, role, ctx) {
  if (node.status !== NAV_STATUS.LIVE) return false;
  const routeOk = Array.isArray(node.roles)
    ? node.roles.includes(role)
    : canAccessRoute(role, routeOf(node.to));
  if (!routeOk) return false;
  if (ctx.menu && Array.isArray(node.menuRoles) && !node.menuRoles.includes(role)) return false;
  if (Array.isArray(node.permission) && !ctx.can(ctx.user, node.permission[0], node.permission[1])) {
    return false;
  }
  return true;
}

function decorateLeaf(node, ctx, level, trail) {
  const locked =
    Boolean(node.module) && ctx.modules !== null && !pharmacyHasModule(ctx.modules, node.module);
  return {
    ...node,
    level,
    trail,
    locked,
    pageLabel: node.pageLabel ?? node.label,
  };
}

function selectNodes(nodes, role, ctx, level, trail) {
  const out = [];
  for (const [position, node] of (nodes ?? []).entries()) {
    // Alt+n is the category's FIXED number in the tree (« Achats · Alt+2 »),
    // whatever the role sees (§6.4).
    const hotkey = ctx.hotkeys && level === 1 ? position + 1 : undefined;
    if (!isContainer(node)) {
      if (leafVisible(node, role, ctx)) {
        out.push({ ...decorateLeaf(node, ctx, level, [...trail, node.label]), ...(hotkey ? { hotkey } : {}) });
      }
      continue;
    }
    const children = selectNodes(node.children, role, ctx, level + 1, [...trail, node.label]);
    if (children.length === 0) continue;
    if (ctx.collapse && level >= 2 && children.length === 1 && !isContainer(children[0])) {
      // A sub-category with one visible page is a direct link carrying the
      // container's label (§6.6); the page keeps its own label for the
      // window title and the breadcrumb (P-04: h1 == title).
      const only = children[0];
      out.push({
        ...only,
        label: node.label,
        pageLabel: only.pageLabel ?? only.label,
        separatorBefore: node.separatorBefore ?? only.separatorBefore,
        collapsedFrom: node.id,
        level,
        trail: [...trail, node.label],
      });
      continue;
    }
    out.push({ ...node, level, trail: [...trail, node.label], children, ...(hotkey ? { hotkey } : {}) });
  }
  return out;
}

function flatFromAdminGroups(groups) {
  return (groups ?? []).map((g, gi) => ({
    id: `admin-flat.${gi}`,
    label: g.label,
    children: (g.items ?? []).map((it) => ({
      id: `admin-flat.${gi}.${it.id}`,
      label: it.label,
      icon: it.icon,
      to: pageToPath(it.page),
      pageKey: it.page,
      status: NAV_STATUS.LIVE,
    })),
  }));
}

/**
 * The menu of a session (§6.6-§6.11).
 * @param {any} user  session user ({ role, modules?, licence? })
 * @param {{ tree?: any[], caissierNav?: any[], adminGroups?: any[], can?: Function, menu?: boolean }} [opts]
 *   `menu: false` keeps the leaves `menuRoles` hides (location resolution of
 *   a page the route admits but the menu does not list, OP-F-09).
 * @returns {{ mode: string, nodes: any[], role: string | null }}
 */
export function selectNavForUser(user, opts = {}) {
  const role = user?.role ?? null;
  const {
    tree = NAV_TREE,
    caissierNav = CAISSIER_NAV,
    adminGroups = NAVIGATION.ADMIN_PHARMAKON,
    can = canDefault,
    menu = true,
  } = opts;
  const ctx = { user, can, modules: userModules(user), menu, collapse: true };
  if (role === ROLES.PROPRIETAIRE || role === ROLES.MANAGER) {
    return { mode: NAV_MODES.TREE, role, nodes: selectNodes(tree, role, { ...ctx, hotkeys: true }, 1, []) };
  }
  if (role === ROLES.CAISSIER) {
    return { mode: NAV_MODES.FLAT, role, nodes: selectNodes(caissierNav, role, { ...ctx, collapse: false }, 1, []) };
  }
  if (role === ROLES.ADMIN_PHARMAKON) {
    return {
      mode: NAV_MODES.FLAT,
      role,
      nodes: selectNodes(flatFromAdminGroups(adminGroups), role, { ...ctx, collapse: false }, 1, []),
    };
  }
  return { mode: NAV_MODES.NONE, role, nodes: [] };
}

// ── Walking ──────────────────────────────────────────────────────────────────

/** Depth-first leaves of a (selected) node list, each with its ancestors. */
export function walkLeaves(nodes, ancestors = [], out = []) {
  for (const n of nodes ?? []) {
    if (isContainer(n)) walkLeaves(n.children, [...ancestors, n], out);
    else out.push({ leaf: n, ancestors });
  }
  return out;
}

/** First leaf under a node (a sub-category crumb links to it, §6.3). */
export function firstLeaf(node) {
  if (!node) return null;
  if (!isContainer(node)) return node;
  for (const c of node.children) {
    const l = firstLeaf(c);
    if (l) return l;
  }
  return null;
}

// ── Active location ──────────────────────────────────────────────────────────

/**
 * Specificity of a route pattern: static segments 10, dynamic 3, splat 1,
 * then the longest path (§6.3 « the resolver »).
 */
export function patternScore(pattern) {
  const segs = routeOf(pattern).split("/").filter(Boolean);
  let score = 0;
  for (const s of segs) score += s === "*" ? 1 : s.startsWith(":") ? 3 : 10;
  return score * 1000 + routeOf(pattern).length;
}

/** Does `pattern` match `pathname` exactly (react-router semantics)? */
export function patternMatches(pattern, pathname) {
  const p = routeOf(pattern);
  if (!p) return false;
  return matchPath({ path: p, end: true, caseSensitive: false }, pathname) != null;
}

/**
 * The leaf the location belongs to, with its ancestors (the rail category
 * first), or null.
 * @param {{ nodes: any[] }} model  selectNavForUser output
 * @param {string} pathname
 * @returns {{ leaf: any, ancestors: any[], pattern: string } | null}
 */
export function resolveActive(model, pathname) {
  const path = routeOf(pathname) || "/";
  let best = null;
  for (const { leaf, ancestors } of walkLeaves(model?.nodes)) {
    for (const pattern of [leaf.to, ...(leaf.match ?? [])]) {
      if (!pattern || !patternMatches(pattern, path)) continue;
      const score = patternScore(pattern);
      if (!best || score > best.score) best = { leaf, ancestors, pattern, score };
    }
  }
  return best ? { leaf: best.leaf, ancestors: best.ancestors, pattern: best.pattern } : null;
}

/** Ids on the current path (rail category, sub-category, leaf) for bolding. */
export function activeIds(active) {
  if (!active) return new Set();
  return new Set([...active.ancestors.map((a) => a.id), active.leaf.id]);
}

// ── Breadcrumb and window title (§6.3) ───────────────────────────────────────

/**
 * Crumbs of the location: the category (`categoryId` — the shell opens its
 * flyout), the sub-category (a link to its first leaf), the page (a link
 * when a document label follows), then the document label (current).
 * A level-1 page alone (Tableau de bord) has no breadcrumb. `flat`: the
 * group of a flat menu (CAISSIER, ADMIN) is plain text.
 * @returns {Array<{ label: string, to?: string, categoryId?: string, current?: boolean }>}
 */
export function breadcrumbFor(active, documentLabel, { flat = false } = {}) {
  if (!active) return [];
  const crumbs = [];
  active.ancestors.forEach((a, i) => {
    // A flat menu's group is a heading, not a flyout: plain text.
    if (i === 0) crumbs.push(flat ? { label: a.label } : { label: a.label, categoryId: a.id });
    else {
      const first = firstLeaf(a);
      crumbs.push(first ? { label: a.label, to: first.to } : { label: a.label });
    }
  });
  const leafLabel = active.leaf.pageLabel ?? active.leaf.label;
  const doc = documentLabel != null && String(documentLabel).trim() !== "" ? String(documentLabel) : null;
  if (crumbs.length === 0 && !doc) return [];
  if (doc) {
    crumbs.push({ label: leafLabel, to: active.leaf.to });
    crumbs.push({ label: doc, current: true });
  } else {
    crumbs.push({ label: leafLabel, current: true });
  }
  return crumbs;
}

/**
 * §6.3 window title: « {leaf.title ?? leaf.label} · {category} — Pharmakon »;
 * a detail page « {document} — {leaf} — Pharmakon »; a level-1 page
 * « {leaf} — Pharmakon ». Null when the location is not a menu page.
 */
export function windowTitleFor(active, documentLabel, appTitle) {
  if (!active) return null;
  const leaf = active.leaf;
  const name = leaf.title ?? leaf.pageLabel ?? leaf.label;
  const doc = documentLabel != null && String(documentLabel).trim() !== "" ? String(documentLabel) : null;
  if (doc) return `${doc} — ${leaf.pageLabel ?? leaf.label} — ${appTitle}`;
  const category = active.ancestors[0];
  if (!category) return `${name} — ${appTitle}`;
  return `${name} · ${category.label} — ${appTitle}`;
}

// ── Flattened leaves (tests, palette) ────────────────────────────────────────

/**
 * The visible leaves of a role (or a session user), in menu order:
 * `{ id, label, menuLabel, title, to, page, keywords, icon, path, locked }`
 * — `page` is the legacy page key (the shape the pre-WP-18 tests iterated).
 * @param {string | any} roleOrUser
 */
export function flattenNav(roleOrUser, opts = {}) {
  const user = typeof roleOrUser === "string" ? { role: roleOrUser } : roleOrUser;
  const model = selectNavForUser(user, opts);
  return walkLeaves(model.nodes).map(({ leaf, ancestors }) => ({
    id: leaf.id,
    label: leaf.pageLabel ?? leaf.label,
    menuLabel: leaf.label,
    title: leaf.title,
    to: leaf.to,
    page: leaf.pageKey,
    keywords: leaf.keywords ?? [],
    icon: leaf.icon ?? ancestors[0]?.icon,
    path: [...ancestors.map((a) => a.label), leaf.label],
    locked: Boolean(leaf.locked),
    module: leaf.module,
    match: leaf.match ?? [],
  }));
}

// ── Keyboard (§6.4, APG menubar) ─────────────────────────────────────────────

/**
 * Type-ahead: the next item whose label starts with `buffer`
 * (accent- and case-insensitive: « tre » finds « Trésorerie »). A single
 * character searches after the current item (repeat to cycle); a longer
 * buffer refines from the current one. Returns -1 when nothing matches.
 */
export function typeAhead(labels, buffer, current) {
  const q = foldText(buffer).trim();
  const n = labels.length;
  if (!q || n === 0) return -1;
  const start = q.length === 1 ? current + 1 : Math.max(current, 0);
  for (let k = 0; k < n; k += 1) {
    const i = (((start + k) % n) + n) % n;
    if (foldText(labels[i]).startsWith(q)) return i;
  }
  return -1;
}

/** ↑ / ↓ (wrapping), Début / Fin within `n` items. */
export function moveIndex(n, i, key) {
  if (n <= 0) return -1;
  switch (key) {
    case "ArrowDown":
      return (i + 1 + n) % n;
    case "ArrowUp":
      return (i - 1 + n) % n;
    case "Home":
      return 0;
    case "End":
      return n - 1;
    default:
      return i;
  }
}

/** Focusable items of a panel (separators are decoration). */
const itemsOf = (node) => (node?.children ?? []);

/**
 * The cascade's keyboard reducer (§6.4 « Keyboard »). The state is the open
 * stack and the focused item:
 *   { open: [categoryId?, subId?], focus: { level: 0 | 1 | 2, index } }
 * level 0 is the rail. Returns the next state and, when the key leaves the
 * cascade, an action: `navigate` (Entrée on a leaf), `exit` (Échap on the
 * rail: focus goes back where it was), or `none`. `handled: false` lets the
 * key through (Tab on the rail with no panel open leaves the rail).
 *
 * @param {any[]} rail   the rail's level-1 nodes
 * @param {{ open: string[], focus: { level: number, index: number } }} state
 * @param {{ key: string, shiftKey?: boolean, ctrlKey?: boolean, metaKey?: boolean }} ev
 */
export function cascadeKey(rail, state, ev) {
  const open = state.open ?? [];
  const focus = state.focus ?? { level: 0, index: 0 };
  const l1 = open[0] ? rail.find((c) => c.id === open[0]) : null;
  const l2 = l1 && open[1] ? itemsOf(l1).find((c) => c.id === open[1]) : null;
  const panelItems = (level) => (level === 0 ? rail : level === 1 ? itemsOf(l1) : itemsOf(l2));
  const items = panelItems(focus.level);
  const current = items[focus.index];
  const key = ev.key;
  const same = (s) => ({ state: s, action: { type: "none" }, handled: true });
  const pass = { state, action: { type: "none" }, handled: false };

  const openChild = (node, level) => {
    // Open a container's panel and focus its item on the current path,
    // else its first item.
    const children = itemsOf(node);
    const cur = children.findIndex((c) => c.__current);
    const nextOpen = level === 0 ? [node.id] : [open[0], node.id];
    return same({ open: nextOpen, focus: { level: level + 1, index: Math.max(cur, 0) } });
  };
  const activate = (node, level) => {
    if (!node) return same(state);
    if (isContainer(node)) return openChild(node, level);
    if (node.to) {
      return {
        state: { open: [], focus: { level: 0, index: focus.level === 0 ? focus.index : railIndexOf(rail, open[0]) } },
        action: { type: "navigate", to: node.to, newTab: Boolean(ev.ctrlKey || ev.metaKey) },
        handled: true,
      };
    }
    return same(state);
  };

  switch (key) {
    case "ArrowDown":
    case "ArrowUp":
    case "Home":
    case "End": {
      const index = moveIndex(items.length, focus.index, key);
      if (focus.level === 0) {
        // A panel already open follows the rail focus (APG menubar).
        const node = rail[index];
        const keepOpen = open.length > 0 && isContainer(node);
        return same({ open: keepOpen ? [node.id] : [], focus: { level: 0, index } });
      }
      // Moving inside level 2 closes an open level 3.
      const nextOpen = focus.level === 1 ? open.slice(0, 1) : open;
      return same({ open: nextOpen, focus: { level: focus.level, index } });
    }
    case "ArrowRight":
      if (current && isContainer(current) && focus.level < 2) return openChild(current, focus.level);
      return same(state);
    case "Enter":
    case " ":
      return activate(current, focus.level);
    case "ArrowLeft":
    case "Escape": {
      if (focus.level === 2) {
        const index = Math.max(itemsOf(l1).findIndex((c) => c.id === open[1]), 0);
        return same({ open: open.slice(0, 1), focus: { level: 1, index } });
      }
      if (focus.level === 1) {
        return same({ open: [], focus: { level: 0, index: railIndexOf(rail, open[0]) } });
      }
      if (key === "Escape") {
        if (open.length > 0) return same({ open: [], focus });
        return { state, action: { type: "exit" }, handled: true };
      }
      return same(state);
    }
    case "Tab": {
      if (focus.level === 0) {
        if (open.length === 0) return pass;
        // Tab from the rail enters the open panel; Maj+Tab closes it and
        // leaves the rail (nothing is trapped behind a closed panel).
        if (ev.shiftKey) return { state: { open: [], focus }, action: { type: "none" }, handled: false };
        const cur = itemsOf(l1).findIndex((c) => c.__current);
        return same({ open, focus: { level: 1, index: Math.max(cur, 0) } });
      }
      // Focus is contained in the open panel stack (§6.4).
      if (!ev.shiftKey && focus.level === 1 && open[1]) {
        const cur = itemsOf(l2).findIndex((c) => c.__current);
        return same({ open, focus: { level: 2, index: Math.max(cur, 0) } });
      }
      if (ev.shiftKey && focus.level === 2) {
        const index = Math.max(itemsOf(l1).findIndex((c) => c.id === open[1]), 0);
        return same({ open, focus: { level: 1, index } });
      }
      const index = moveIndex(items.length, focus.index, ev.shiftKey ? "ArrowUp" : "ArrowDown");
      return same({ open, focus: { level: focus.level, index } });
    }
    default:
      return pass;
  }
}

function railIndexOf(rail, id) {
  return Math.max(
    rail.findIndex((c) => c.id === id),
    0,
  );
}

/**
 * Mark the nodes on the current path (`__current`) so the keyboard opens a
 * panel on the page's own item (§6.4 « focus the sub-category on the current
 * path, else the first one »). Returns new node objects.
 */
export function markCurrent(nodes, ids) {
  return (nodes ?? []).map((n) => {
    const marked = { ...n, __current: ids.has(n.id) };
    if (isContainer(n)) marked.children = markCurrent(n.children, ids);
    return marked;
  });
}

// ── Menu aim (§6.4) ──────────────────────────────────────────────────────────

/**
 * True while the pointer travels from `prev` towards the child panel: the
 * point `cur` lies inside the triangle formed by `prev` and the panel's two
 * near corners (`rect`: { left, top, bottom }). Siblings do not switch then.
 */
export function inAimTriangle(prev, cur, rect) {
  if (!prev || !cur || !rect) return false;
  if (cur.x >= rect.left) return false;
  const a = prev;
  const b = { x: rect.left, y: rect.top };
  const c = { x: rect.left, y: rect.bottom };
  const sign = (p1, p2, p3) => (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  const d1 = sign(cur, a, b);
  const d2 = sign(cur, b, c);
  const d3 = sign(cur, c, a);
  const neg = d1 < 0 || d2 < 0 || d3 < 0;
  const pos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(neg && pos);
}

// ── NotFound (§3.12, §6.3) ───────────────────────────────────────────────────

/**
 * Menu pages close to an unknown address: the visible leaves whose route
 * shares its first segment (« /achats/xyz » → the Achats pages under
 * `/achats…`), in menu order, at most `limit`.
 */
export function nearbyPages(model, pathname, limit = 5) {
  const first = routeOf(pathname).split("/").filter(Boolean)[0];
  if (!first) return [];
  return walkLeaves(model?.nodes)
    .map(({ leaf, ancestors }) => ({ leaf, ancestors }))
    .filter(({ leaf }) => routeOf(leaf.to).split("/").filter(Boolean)[0] === first)
    .slice(0, limit)
    .map(({ leaf, ancestors }) => ({
      id: leaf.id,
      label: leaf.pageLabel ?? leaf.label,
      to: leaf.to,
      path: [...ancestors.map((a) => a.label), leaf.pageLabel ?? leaf.label].join(" › "),
    }));
}

/**
 * The document label a PageHeader contributes (§6.3 « for detail pages … ›
 * {document label} »): the explicit `documentLabel`, else — on a page reached
 * through a leaf's `match` pattern (a detail or sub-page, not the leaf's own
 * route) — the last crumb the page itself names (« FA-2026-0142 », « Modifier »).
 */
export function documentLabelFor(active, explicitLabel, pageCrumbs) {
  if (explicitLabel != null && String(explicitLabel).trim() !== "") return String(explicitLabel);
  if (!active || active.pattern === active.leaf.to) return null;
  const last = Array.isArray(pageCrumbs) ? pageCrumbs[pageCrumbs.length - 1] : null;
  const label = last?.label;
  return typeof label === "string" || typeof label === "number" ? String(label) : null;
}
